function to_array(value) {
  return Array.isArray(value) ? value : [];
}

function normalize_text(value) {
  return String(value || '').trim();
}

function first_value(values = []) {
  return values.find(Boolean);
}

function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function first_order_value(item = {}, fallback_order = 0) {
  const raw_value = [
    item?.order_number,
    item?.order,
    item?.priority,
    item?.sort_order,
    fallback_order,
  ].find((value) => Number.isFinite(Number(value)) && Number(value) > 0);

  return Number(raw_value || fallback_order || 0);
}

const TENANT_INTEGRATION_CATALOG = [
  { key: 'saps_reference', title: 'SAPS Reference Verify', description: 'Auto-checks incident references on intake' },
  { key: 'home_affairs', title: 'Home Affairs ID Verify', description: 'SA ID + photo match for requesters', setup_order: 3 },
  { key: 'whatsapp_business', title: 'WhatsApp Business', description: 'Two-way messaging on a verified number', setup_order: 2 },
  { key: 'bank_eft', title: 'FNB / Standard Bank EFT', description: 'For approved payouts', setup_order: 4 },
  { key: 'microsoft_365', title: 'Microsoft 365 (SSO)', description: 'Single sign-on for staff', setup_order: 1 },
  { key: 'xero', title: 'Xero · Accounting', description: 'Push invoices to accounting' },
  { key: 'docusign', title: 'DocuSign', description: 'e-Signature for decision letters' },
  { key: 'slack', title: 'Slack · Alerts', description: 'Channel notifications for SLA breaches' },
];

export function tenantIntegrationOptions({ setup_only = false } = {}) {
  return TENANT_INTEGRATION_CATALOG
    .filter((integration) => !setup_only || integration.setup_order)
    .sort((left, right) => setup_only ? left.setup_order - right.setup_order : 0)
    .map((integration) => ({ ...integration }));
}

export function mapTenantIntegrations(selected_integrations = []) {
  const selected = new Set(to_array(selected_integrations).map(normalize_text));

  return tenantIntegrationOptions().map((integration) => ({
    ...integration,
    connected: selected.has(integration.key),
  }));
}

export function entityConfigFromPayload(config_data = {}) {
  return config_data?.entity_config || config_data || {};
}

export function entityTypeIdFromConfig(config_data = {}) {
  return to_number(entityConfigFromPayload(config_data)?.entity_type);
}

export function entityTypeNameFromConfig(config_data = {}) {
  const config = entityConfigFromPayload(config_data);
  return normalize_text(first_value([
    config?.entity_type_name,
    config?.entity_type_label,
    config?.domain_name,
    config?.domain,
    config?.entity_type,
    config?.name,
    config?.label,
  ]));
}

export function activeFormBindings(config_data = {}) {
  return to_array(config_data?.form_bindings)
    .filter((binding) => binding?.is_active !== false)
    .sort((left, right) => first_order_value(left, 1) - first_order_value(right, 1));
}

export function activeRiskBindings(config_data = {}) {
  return to_array(config_data?.risk_bindings)
    .filter((binding) => binding?.is_active !== false);
}

export function activeWorkflowBindings(config_data = {}) {
  return to_array(config_data?.workflow_bindings)
    .filter((binding) => binding?.is_active !== false);
}

export function schemaQuestions(config_data = {}) {
  return to_array(entityConfigFromPayload(config_data)?.entity_schema?.questions);
}

export function bindingSummary(config_data = {}) {
  return entityConfigFromPayload(config_data)?.entity_schema?.binding_summary || {};
}

function active_binding_count(bindings) {
  return to_array(bindings).filter((binding) => binding?.is_active !== false).length;
}

function configured_count(bindings, summary_count) {
  return active_binding_count(bindings) || Number(summary_count || 0);
}

export function configCounts(config_data = {}) {
  const summary = bindingSummary(config_data);

  return {
    fields_count: schemaQuestions(config_data).length,
    form_count: configured_count(config_data?.form_bindings, summary.entity_form_binding),
    workflow_count: configured_count(config_data?.workflow_bindings, summary.entity_workflow_binding),
    risk_count: configured_count(config_data?.risk_bindings, summary.entity_risk_binding),
    rule_count: configured_count(config_data?.rule_bindings, summary.entity_rule_binding),
    document_count: configured_count(config_data?.document_bindings, summary.entity_document_binding),
    sla_count: configured_count(config_data?.sla_bindings, summary.entity_sla_binding),
  };
}

export function mapEntityConfigToCaseTypeOption(config_data = {}) {
  const entity_config = entityConfigFromPayload(config_data);
  const entity_type_id = entityTypeIdFromConfig(config_data);
  const form_ids = activeFormBindings(config_data)
    .map((form_binding) => to_number(form_binding?.form || form_binding?.form_id))
    .filter(Boolean);

  if (!entity_type_id) {
    return null;
  }

  const title = entityTypeNameFromConfig(config_data);
  const meta = form_ids.length
    ? `${form_ids.length} assigned form${form_ids.length === 1 ? '' : 's'}`
    : '';
  return {
    id: entity_type_id,
    title: first_value([title, `Case type ${entity_type_id}`]),
    meta,
    entity_config_id: to_number(entity_config?.id),
    form_ids,
    config_data,
  };
}

export function mapEntityConfigToTenantDomainOption(config_data = {}) {
  const entity_config = entityConfigFromPayload(config_data);
  const entity_type_id = entityTypeIdFromConfig(config_data);

  if (!entity_type_id) {
    return null;
  }

  return {
    key: entity_type_id,
    config_id: to_number(entity_config?.id),
    title: entityTypeNameFromConfig(config_data) || `Entity type ${entity_type_id}`,
    description: tenant_domain_description(entity_config, config_data),
  };
}

function tenant_domain_description(entity_config = {}, config_data = {}) {
  const descriptions = [
    entity_config?.entity_schema?.description,
    entity_config?.entity_type_description,
    config_data?.entity_type_description,
    'Available case domain',
  ];
  return normalize_text(first_value(descriptions));
}

export function mapEntityConfigToEntityType(config_data = {}) {
  const entity_config = entityConfigFromPayload(config_data);
  const entity_type_id = entityTypeIdFromConfig(config_data);

  if (!entity_type_id) {
    return null;
  }

  return {
    ...entity_config,
    id: entity_type_id,
    entity_config_id: to_number(entity_config?.id),
    entity_type_name: entityTypeNameFromConfig(config_data) || `Case type ${entity_type_id}`,
    config_data,
  };
}

export function mapRiskBindingsToAssessments(config_data = {}) {
  return activeRiskBindings(config_data)
    .map((binding) => ({
      id: risk_assessment_id_from_binding(binding),
      entity_risk_framework_assessment_id: to_number(binding?.entity_risk_framework_assessment),
      binding_id: to_number(binding?.binding_id || binding?.id),
      risk_assessment: normalize_text(binding?.assessment_name),
      assessment_name: normalize_text(binding?.assessment_name),
      assessment_phase: normalize_text(binding?.assessment_phase),
      source_binding: binding,
    }))
    .filter((assessment) => assessment.id);
}

function risk_assessment_id_from_binding(binding = {}) {
  return [
    binding?.risk_framework_assessment_id,
    binding?.risk_framework_assessment?.id,
    binding?.risk_framework_assessment,
    binding?.assessment?.id,
    binding?.assessment_id,
    binding?.entity_risk_framework_assessment,
  ].map(to_number).find(Boolean);
}
